import { Button, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTER } from '../../../constants/router';
import ListPrivacy from '../Shared/ListPrivacy';
import useNotify from '../../../context/Notification/useNotify';
import { useForm } from 'antd/lib/form/Form';
import DocTabs from './components/DocTabs';
import { usePrivacytoString } from './components/PrivacytoString';
import { PlusOutlined } from '@ant-design/icons';
import CCTVPolicyService from '../../../services/CCTVPolicy.service';
import { POLICY_NAME } from '../POLICY';
import GlobalService from '../../../services/Global.service';
import { calculateImageWidth } from '../../../util/util';
import usePermission from '../../../hooks/usePermission';

const CCTVPolicy = ({ selectType }) => {
    const { t, i18n } = useTranslation([
        'translation',
        POLICY_NAME.CCTV_POLICY,
    ]);
    let lang = i18n.language;
    const [data, setData] = useState([]);
    const [currentSelectList, setCurrentSelectList] = useState();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState();
    const [detailPublished, setDetailPublished] = useState();
    const [logo, setLogo] = useState('');
    const [logoPublish, setLogoPublish] = useState('');
    const getImage = async (companyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(companyLogoId);
            if (data.resultData?.imageId) {
                return `data:image/png;base64,${data.resultData?.imageId}`;
            } else {
                setLogo('');
                setLogoPublish('');
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        const getPolicy = async () => {
            setLoading(true);
            try {
                const { data } = await CCTVPolicyService.getCCTVDetail(
                    currentSelectList?.cctvPolicyNoticeId
                );
                if (data?.resultData) {
                    setDetail(data?.resultData);
                    setLoading(false);
                    setLogo('');
                    if (data?.resultData?.cctvPolicy?.cctvCompanyLogoId) {
                        getImage(
                            data?.resultData?.cctvPolicy?.cctvCompanyLogoId
                        ).then((logo) => setLogo(logo));
                    }
                } else setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };
        const getPublishedVersion = async () => {
            try {
                const { data } = await CCTVPolicyService.getPublishedVersion(
                    currentSelectList?.cctvPolicyNoticeId
                );
                if (data?.resultData) {
                    const parse = JSON.parse(data?.resultData?.data);
                    setDetailPublished(parse);
                    // Logo
                    setLogoPublish('');
                    if (parse?.cctvPolicy?.cctvCompanyLogoId) {
                        getImage(parse?.cctvPolicy?.cctvCompanyLogoId).then(
                            (logo) => setLogoPublish(logo)
                        );
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (currentSelectList?.cctvPolicyNoticeId) getPolicy();
        if (
            currentSelectList?.hasChanged &&
            currentSelectList?.status === 'Published'
        )
            getPublishedVersion();
        else setDetailPublished();
    }, [currentSelectList]);

    let navigate = useNavigate();

    const getPolicyList = async () => {
        const query = {
            dataSubjectTypeIds: selectType ? selectType : '',
        };
        const { data } = await CCTVPolicyService.searchCCTV(query);
        if (data.resultData) {
            setData(data.resultData);
            setCurrentSelectList(data.resultData[0]);
        }
    };

    useEffect(() => {
        getPolicyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectType]);

    const notify = useNotify();
    const [form] = useForm();

    const handelRename = (values) => {
        setLoading(true);
        if (currentSelectList?.cctvPolicyNoticeId) {
            const payload = {
                cctvPolicyId: currentSelectList?.cctvPolicyNoticeId,
                cctvPolicyNameEn: values.nameEn,
                cctvPolicyNameTh: values.nameTh,
            };
            setTimeout(async () => {
                try {
                    const { data } = await CCTVPolicyService.rename(payload);
                    if (data.resultCode === 1) {
                        form.resetFields();
                        setTimeout(() => {
                            setLoading(false);
                            getPolicyList();
                        }, 1300);
                    } else {
                        setLoading(false);
                        notify({
                            type: 'error',
                            title: t('common.Fail'),
                            description: t('Failed to rename'),
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to rename'),
                    });
                }
            }, 1000);
        }
    };
    const width1 = calculateImageWidth(logo);
    const width2 = calculateImageWidth(logoPublish);

    const { html } = usePrivacytoString(detail, 'TH', logo, width1);
    const { html: htmlEn } = usePrivacytoString(detail, 'EN', logo, width1);
    const { html: htmlPublish } = usePrivacytoString(
        detailPublished,
        'TH',
        logoPublish,
        width2
    );
    const { html: htmlPublishEn } = usePrivacytoString(
        detailPublished,
        'EN',
        logoPublish,
        width2
    );

    const { isAuditor } = usePermission();

    return (
        <Layout>
            <div className="flex items-center my-4">
                <div className="flex-1 font-bold text-xl">
                    {t('CCTV Policy list', { ns: POLICY_NAME.CCTV_POLICY })}
                </div>
                <Button
                    disabled={isAuditor}
                    type="primary"
                    size="large"
                    onClick={() => {
                        navigate(`${ROUTER.POLICY.CCTV_PRIVACY}/new#step1`);
                    }}
                    icon={<PlusOutlined />}
                >
                    {t('Add Policy')}
                </Button>
            </div>
            <div className="grid grid-cols-7 gap-4">
                <ListPrivacy
                    data={data}
                    pathEdit={ROUTER.POLICY.CCTV_PRIVACY}
                    stepEdit={'#step9'} // step preview
                    currentSelectName={{
                        nameTh: detail?.cctvPolicy?.cctvPolicyNameTh,
                        nameEn: detail?.cctvPolicy?.cctvPolicyNameEn,
                        namePublishTh:
                            detailPublished?.cctvPolicy?.cctvPolicyNameTh,
                        namePublishEn:
                            detailPublished?.cctvPolicy?.cctvPolicyNameEn,
                        placeholderNameEN: `${t('Name of CCTV Policy', {
                            ns: POLICY_NAME.CCTV_POLICY,
                        })} (EN)`,
                        placeholderNameTH: `${t('Name of CCTV Policy', {
                            ns: POLICY_NAME.CCTV_POLICY,
                        })} (TH)`,
                    }}
                    linkName="external-cctv-policy"
                    getPolicyList={getPolicyList}
                    setCurrentSelectList={setCurrentSelectList}
                    currentSelectList={currentSelectList}
                    form={form}
                    // Parameters Name
                    policyObject={{
                        privacyName:
                            lang === 'en'
                                ? 'cctvPolicyNoticeNameEn'
                                : 'cctvPolicyNoticeNameTh',
                        nameTh: 'cctvPolicyNoticeNameTh',
                        nameEn: 'cctvPolicyNoticeNameEn',
                        privacyType:
                            lang === 'en'
                                ? 'cctvPolicyTypeEn'
                                : 'cctvPolicyTypeTh',
                        privacyId: 'cctvPolicyNoticeId',
                        privacyCompany:
                            lang === 'en'
                                ? 'cctvCompanyNameEn'
                                : 'cctvCompanyNameTh',
                    }}
                    handelRename={(value) => handelRename(value)}
                    historyObject={{
                        logo: logo,
                        serviceHistory: CCTVPolicyService.getHistoriesCCTV,
                        type: 'cctv',
                        historyParamName: 'cctvPolicyId', // ชื่อ payload ของ id ที่ service get history เช่น hrPrivacyPolicyId: "1234" ที่ต้องใส่คือ: hrPrivacyPolicyId
                        historyResultIdName: 'cctvPolicyHistoryId', // ได้หลังจาก service get history ใน result ชื่อ parameter ของ id เช่น { "hrPrivacyPolicyHistoryId" : 1234 } ที่ต้องใส่คือ: hrPrivacyPolicyHistoryId
                    }}
                    deleteObject={{
                        serviceDelete: CCTVPolicyService.delete,
                        title: lang === 'en' ? 'Confirm Delete' : 'ยืนยันการลบ',
                        text:
                            currentSelectList?.status === 'Draft'
                                ? lang === 'en'
                                    ? `Do you want to delete draft version ${currentSelectList?.version} ?`
                                    : `คุณต้องการลบรายการแบบร่างเวอร์ชัน ${currentSelectList?.version} ?`
                                : lang === 'en'
                                ? `<span style='color:#F0536F'>The use of the URL link for this CCTV policy will still be available, but cannot be edited anymore.</span>\nDo you want to delete publish version ${currentSelectList?.latestPublishVersion} ?`
                                : `<span style='color:#F0536F'>การใช้งาน URL link นโยบายความเป็นส่วนตัวสำหรับกล้องวงจรปิดนี้จะยังคงใช้งานได้ แต่จะไม่สามารถแก้ไขนโยบายความเป็นส่วนตัวสำหรับกล้องวงจรปิดได้อีก</span>\nคุณต้องการลบรายการเผยแพร่เวอร์ชัน ${currentSelectList?.latestPublishVersion} ?`,
                    }}
                    // pop up ของ confirm ก่อน delete
                    confirmObject={{
                        title:
                            lang === 'en'
                                ? 'There may be websites that use this CCTV policy.'
                                : 'อาจมีเว็บไซต์ใช้นโยบายความเป็นส่วนตัว สำหรับกล้องวงจรปิดนี้อยู่',
                        text:
                            lang === 'en'
                                ? `Before deleting the data, please check that no website is using this CCTV policy.`
                                : `ก่อนลบข้อมูล โปรดตรวจสอบว่าไม่มีเว็บไซต์ใดใช้\nนโยบายความเป็นส่วนตัวสำหรับกล้องวงจรปิดนี้`,
                    }}
                    loading={loading}
                    templateBody={{
                        html,
                        htmlEn: htmlEn,
                        htmlPublish: htmlPublish,
                        htmlPublishEn: htmlPublishEn,
                    }}
                >
                    {(previewLang) => (
                        <DocTabs
                            logo={logo}
                            logoPublish={logoPublish}
                            detailPublished={detailPublished}
                            detail={detail}
                            loading={loading}
                            currentSelectList={currentSelectList}
                            previewLang={previewLang}
                            policyId={currentSelectList?.cctvPolicyNoticeId}
                        />
                    )}
                </ListPrivacy>
            </div>
        </Layout>
    );
};

export default CCTVPolicy;
